import { type ItemOf } from '@shared/board'
import { clsx } from 'clsx'
import { EditableTitle } from '@/components/EditableTitle'
import { useI18n } from '@/i18n/i18n'
import { DRAG_HANDLE, NO_DRAG } from '../classes'
import { useBoardCtx, useTitleEditing } from '../context'

/**
 * A group's frame. Only the title bar and the edge strips are solid: they move
 * the group (and so its members). The interior lets clicks through to whatever
 * is under it, or to the board for a marquee.
 */
export function GroupView({ item, selected }: { item: ItemOf<'group'>; selected: boolean }) {
  const { client } = useBoardCtx()
  const titleEditing = useTitleEditing()
  const editing = titleEditing.id === item.id
  const setEditing = (on: boolean) => {
    titleEditing.set(on ? item.id : null)
  }
  const { t } = useI18n()
  return (
    <div className="moot-group pointer-events-none relative size-full">
      <div
        className={clsx(
          'absolute inset-0 rounded-xl border-[1.5px] transition-colors',
          selected ? 'border-(--focus) bg-ai-soft/40' : 'border-line-strong bg-(--group-fill)',
        )}
      />
      {/* Edge strips: generous, invisible grab bands along the frame. */}
      {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
        <div
          key={side}
          className={clsx(
            DRAG_HANDLE,
            'moot-group-strip pointer-events-auto absolute',
            `is-${side}`,
          )}
        />
      ))}
      <div
        className={clsx(
          DRAG_HANDLE,
          'moot-group-title pointer-events-auto absolute -top-3.5 left-3 z-[3] flex max-w-[calc(100%-1.5rem)] cursor-move items-center rounded-sm bg-canvas px-2 py-0.5',
        )}
        onDoubleClick={(e) => {
          e.stopPropagation()
          if (!client.readOnly) setEditing(true)
        }}
      >
        <EditableTitle
          value={editing ? item.data.title : item.data.title || t('group.untitled')}
          editing={editing}
          onEditingChange={setEditing}
          maxLength={200}
          onCommit={(title) => {
            client.apply([{ type: 'item.update', id: item.id, patch: { data: { title } } }])
          }}
          className={clsx(
            'truncate text-[12.5px] font-semibold tracking-[0.02em]',
            item.data.title ? 'text-ink-2' : 'text-muted',
          )}
          inputClassName={clsx(NO_DRAG, 'text-[12.5px] font-semibold tracking-[0.02em]')}
          autosize
          placeholder={t('group.untitled')}
        />
      </div>
    </div>
  )
}
